import { getMCPMonitorDistribution } from "@/api/endpoints/searches";
import { Source, SOURCE_CODE_TO_LABEL } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { SearchFilterV4WithDates } from "@/types/SearchFilter";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetProps,
  WidgetTitle,
} from "@components/ui/widget";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { getMCPMonitorFiltersFromSearchFilterV4 } from "./utils";
import { Progress } from "@components/ui/progress";
import { cn } from "@/lib/utils";

export default function ChannelTypesByMentionCountWidget({
  searchUuid,
  className,
  filters,
  ...rest
}: {
  filters: SearchFilterV4WithDates;
  searchUuid: Uuid;
} & WidgetProps) {
  const { formatPercent } = useI18n();

  // Server state
  const {
    data: channelTypeDistribution,
    isLoading: isChannelTypeDistributionLoading,
  } = useQuery({
    queryKey: [searchUuid, "channel-type-distribution", filters],
    queryFn: () =>
      getMCPMonitorDistribution(searchUuid, {
        ...getMCPMonitorFiltersFromSearchFilterV4(filters),
        metric: "mention_count",
      }),
  });

  const shares = channelTypeDistribution?.ok
    ? Object.entries(channelTypeDistribution.data.shares)
        .filter(([, value]) => !!value)
        .toSorted((a, b) => {
          return b[1] - a[1];
        })
    : undefined;

  // Helpers
  const isLoading = isChannelTypeDistributionLoading;

  return (
    <Widget className={cn("h-62", className)} {...rest}>
      <WidgetHeader isLoading={isLoading} skeletonClassName="h-6">
        <WidgetTitle className="font-medium">
          <Trans>Fördelning per medietyp</Trans>
        </WidgetTitle>
      </WidgetHeader>
      <WidgetContent
        isLoading={isLoading}
        className="divide-y gap-0 overflow-y-auto"
        isEmpty={
          !isLoading && (!channelTypeDistribution?.ok || !shares?.length)
        }
      >
        {shares
          ? shares.map(([source, share]) => (
              <div
                key={source}
                className="flex items-center py-2 first:pt-0 last:pb-0 gap-2.5"
              >
                <p className="flex-1 truncate text-muted-foreground text-sm">
                  {i18n._(SOURCE_CODE_TO_LABEL[source as Source])}
                </p>
                <Progress className="flex-1" value={share * 100} />
                <p className="text-sm text-center font-medium min-w-14">
                  {formatPercent(share, 0)}
                </p>
              </div>
            ))
          : null}
      </WidgetContent>
    </Widget>
  );
}
